<template>
  <transition name="fade">
    <div v-if="isVisible" class="extrusion-edit-modal-overlay">
      <div class="extrusion-edit-modal-dialog">
        <!-- Header -->
        <div class="modal-dialog-header">
          <div class="d-flex align-items-center gap-1">
            <feather-icon icon="Edit3Icon" size="22" class="text-copper" />
            <h4 class="modal-dialog-title m-0">{{ modalTitle }}</h4>
          </div>
          <button class="btn-close-modal" @click="closeModal">
            <feather-icon icon="XIcon" size="22" />
          </button>
        </div>

        <!-- Body -->
        <div class="modal-dialog-body font-raleway">
          <!-- OP & Status Banner -->
          <div
            class="op-header-info p-2 mb-3 rounded border bg-light d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
              <feather-icon icon="LayersIcon" size="20" class="text-copper mr-1" />
              <div>
                <div class="small text-muted font-weight-bold">Ordem de Produção</div>
                <div class="h5 font-weight-bold text-dark mb-0">#{{ orderCode || '-' }}</div>
              </div>
            </div>
            <span class="status-chip" :style="getStatusBadgeStyle(item ? item.statusColor : '#974900')">
              {{ item ? (item.statusName || item.appointmentType) : 'Apontamento' }}
            </span>
          </div>

          <!-- 1. Form de Tarugo / Extrusão -->
          <b-row v-if="editType === 'Tarugo'" class="form-grid">
            <!-- Liga (Select) -->
            <b-col cols="12" sm="6" md="4" class="mb-2">
              <label class="input-label">Liga <span class="text-danger">*</span></label>
              <select v-model="form.alloy" class="operator-input w-100 cursor-pointer">
                <option value="" disabled>Selecione a Liga</option>
                <option v-for="opt in computedAlloyOptions" :key="opt" :value="opt">{{ opt }}</option>
              </select>
            </b-col>

            <!-- Origem (Select) -->
            <b-col cols="12" sm="6" md="4" class="mb-2">
              <label class="input-label">Origem <span class="text-danger">*</span></label>
              <select v-model="form.origin" class="operator-input w-100 cursor-pointer">
                <option value="" disabled>Selecione a Origem</option>
                <option v-for="opt in computedOriginOptions" :key="opt" :value="opt">{{ opt }}</option>
              </select>
            </b-col>

            <!-- Fusão / Corrida -->
            <b-col cols="12" sm="6" md="4" class="mb-2">
              <label class="input-label">Fusão / Corrida <span class="text-danger">*</span></label>
              <input type="text" v-model="form.heatNumber" class="operator-input w-100" placeholder="Ex: FUS-9912" />
            </b-col>

            <!-- Tamanho Tarugo (mm) -->
            <b-col cols="12" sm="6" md="4" class="mb-2">
              <label class="input-label">Tamanho tarugo (mm) <span class="text-danger">*</span></label>
              <input type="number" step="0.1" v-model.number="form.sizeMm" class="operator-input w-100"
                placeholder="Ex: 500.0" />
            </b-col>

            <!-- Qtd. Tarugos -->
            <b-col cols="12" sm="6" md="4" class="mb-2">
              <label class="input-label">Qtd. Tarugos <span class="text-danger">*</span></label>
              <input type="number" step="1" v-model.number="form.quantity"
                class="operator-input w-100 font-weight-bold text-primary" placeholder="Ex: 10" />
            </b-col>

            <!-- Qtd. Barras -->
            <b-col cols="12" sm="6" md="4" class="mb-2">
              <label class="input-label">Qtd. Barras <span class="text-danger">*</span></label>
              <input type="number" step="1" v-model.number="form.barQuantity"
                class="operator-input w-100 font-weight-bold text-success" placeholder="Ex: 100" />
            </b-col>

            <!-- Puxada (m) -->
            <b-col cols="12" sm="6" md="4" class="mb-2">
              <label class="input-label">Comprimento Puxada (m) <span class="text-danger">*</span></label>
              <input type="number" step="0.1" v-model.number="form.pullLengthM" class="operator-input w-100"
                placeholder="Ex: 6.0" />
            </b-col>

            <!-- Temp. Tarugo (°C) -->
            <b-col cols="12" sm="6" md="4" class="mb-2">
              <label class="input-label">Temp. Tarugo (°C) <span class="text-danger">*</span></label>
              <input type="number" step="1" v-model.number="form.temperature" class="operator-input w-100 text-orange"
                placeholder="Ex: 480" />
            </b-col>

            <!-- Temp. Emergente (°C) -->
            <b-col cols="12" sm="6" md="4" class="mb-2">
              <label class="input-label">Temp. Emergente (°C) <span class="text-danger">*</span></label>
              <input type="number" step="1" v-model.number="form.emergentTemperature"
                class="operator-input w-100 text-red" placeholder="Ex: 510" />
            </b-col>

            <!-- Vel. Extrusão (m/min) -->
            <b-col cols="12" sm="6" md="4" class="mb-2">
              <label class="input-label">Vel. Extrusão (m/min)</label>
              <input type="number" step="0.1" v-model.number="form.extrusionSpeedMMin" class="operator-input w-100"
                placeholder="Ex: 30.0" />
            </b-col>

            <!-- Peso Perfil (kg/m) -->
            <b-col cols="12" sm="6" md="4" class="mb-2">
              <label class="input-label">Peso Perfil (kg/m)</label>
              <input type="number" step="0.001" v-model.number="form.profileWeightKgM" class="operator-input w-100"
                placeholder="Ex: 2.500" />
            </b-col>

            <!-- Observação -->
            <b-col cols="12" class="mb-2">
              <label class="input-label">Observação</label>
              <textarea v-model="form.observation" rows="2" class="operator-input w-100"
                style="height: auto; padding-top: 8px;"
                placeholder="Observações do apontamento (opcional)..."></textarea>
            </b-col>
          </b-row>

          <!-- 2. Form de Finalização da Extrusão -->
          <b-row v-else-if="editType === 'Finalizacao'" class="form-grid">
            <!-- Situação -->
            <b-col cols="12" sm="6" class="mb-2">
              <label class="input-label">Situação <span class="text-danger">*</span></label>
              <select v-model="form.situation" class="operator-input w-100">
                <option value="Aprovado">Aprovado</option>
                <option value="Reprovado">Reprovado</option>
                <option value="Aprovado com Restrição">Aprovado com Restrição</option>
              </select>
            </b-col>

            <!-- Motivo de Reprovação (se Reprovado) -->
            <b-col v-if="form.situation === 'Reprovado'" cols="12" sm="6" class="mb-2">
              <label class="input-label">Motivo de Reprovação</label>
              <select v-model="form.rejectionReasonId" class="operator-input w-100" @change="onRejectionReasonChange">
                <option :value="null">Selecione um motivo...</option>
                <option v-for="r in refuseReasons" :key="r.id" :value="r.id">{{ r.name }}</option>
              </select>
            </b-col>

            <!-- Observação -->
            <b-col cols="12" class="mb-2">
              <label class="input-label">Observação / Justificativa</label>
              <textarea v-model="form.observation" rows="3" class="operator-input w-100"
                style="height: auto; padding-top: 8px;" placeholder="Observações da finalização..."></textarea>
            </b-col>
          </b-row>

          <!-- 3. Form de Cesto do Corte -->
          <b-row v-else-if="editType === 'CorteCesto'" class="form-grid">
            <!-- Código do Cesto -->
            <b-col cols="12" sm="6" class="mb-2">
              <label class="input-label">Código / Identificação do Cesto <span class="text-danger">*</span></label>
              <input type="text" v-model="form.basketCode" class="operator-input w-100 text-primary font-weight-bold"
                placeholder="Ex: C-01" />
            </b-col>

            <!-- Quantidade de Barras -->
            <b-col cols="12" sm="6" class="mb-2">
              <label class="input-label">Qtd. de Barras no Cesto <span class="text-danger">*</span></label>
              <input type="number" step="1" v-model.number="form.barQuantity"
                class="operator-input w-100 font-weight-bold" placeholder="Ex: 132" />
            </b-col>
          </b-row>

          <!-- 4. Form de Refugo do Corte -->
          <b-row v-else-if="editType === 'CorteRefugo'" class="form-grid">
            <!-- Peso do Refugo (kg) -->
            <b-col cols="12" sm="6" class="mb-2">
              <label class="input-label">Peso Refugado (kg) <span class="text-danger">*</span></label>
              <input type="number" step="0.01" v-model.number="form.quantityKg"
                class="operator-input w-100 text-danger font-weight-bold" placeholder="Ex: 15.50" />
            </b-col>

            <!-- Quantidade de Barras -->
            <b-col cols="12" sm="6" class="mb-2">
              <label class="input-label">Barras Refugadas (un)</label>
              <input type="number" step="1" v-model.number="form.quantityBars" class="operator-input w-100"
                placeholder="Ex: 2" />
            </b-col>

            <!-- Motivo de Refugo -->
            <b-col cols="12" class="mb-2">
              <label class="input-label">Motivo do Refugo <span class="text-danger">*</span></label>
              <select v-model="form.refuseReasonId" class="operator-input w-100">
                <option :value="null">Selecione o motivo...</option>
                <option v-for="r in refuseReasons" :key="r.id" :value="r.id">{{ r.name }}</option>
              </select>
            </b-col>

            <!-- Observação -->
            <b-col cols="12" class="mb-2">
              <label class="input-label">Observação</label>
              <textarea v-model="form.observation" rows="2" class="operator-input w-100"
                style="height: auto; padding-top: 8px;" placeholder="Observações do refugo (opcional)..."></textarea>
            </b-col>
          </b-row>

          <!-- 5. Form de Status / Parada -->
          <b-row v-else class="form-grid">
            <!-- Motivo da Parada / Status -->
            <b-col cols="12" class="mb-2">
              <label class="input-label">Status / Motivo da Parada <span class="text-danger">*</span></label>
              <select v-model="form.statusId" class="operator-input w-100 cursor-pointer">
                <option v-for="s in stopStatusList" :key="s.id" :value="s.id">{{ s.name }}</option>
              </select>
            </b-col>
          </b-row>
        </div>

        <!-- Footer -->
        <div class="modal-dialog-footer d-flex align-items-center justify-content-end gap-2 p-2 border-top">
          <button class="btn-operator-secondary px-3 mr-1" @click="closeModal" :disabled="submitting">
            Cancelar
          </button>
          <button class="btn-operator-confirm px-4" :disabled="!isFormValid || submitting" @click="submitEdit">
            <b-spinner v-if="submitting" small class="mr-50" />
            <feather-icon v-else icon="CheckIcon" size="18" class="mr-50" />
            <span>Salvar Alterações</span>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { BRow, BCol, BSpinner } from 'bootstrap-vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import {
  updateExtrusionProgress,
  updateCuttingBasket,
  updateCuttingRefuse,
  getExtrusionAlloys
} from '../extrusionApi';

export default {
  name: 'ExtrusionEditAppointmentModal',
  components: {
    BRow,
    BCol,
    BSpinner
  },
  props: {
    orderCode: {
      type: [String, Number],
      default: ''
    },
    siteId: {
      type: [Number, String],
      default: null
    },
    orderObject: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      isVisible: false,
      submitting: false,
      item: null,
      refuseReasons: [],
      statusList: [],
      alloyOptions: [],
      form: {}
    };
  },
  computed: {
    resolvedSiteId() {
      if (this.siteId) {
        if (typeof this.siteId === 'number') return this.siteId;
        if (!isNaN(this.siteId)) return parseInt(this.siteId, 10);
      }
      const cookieSite = this.$cookies?.get('userInfo')?.currentSite;
      if (cookieSite) {
        if (typeof cookieSite === 'object' && cookieSite.id) return cookieSite.id;
        if (typeof cookieSite === 'number') return cookieSite;
        if (!isNaN(cookieSite)) return parseInt(cookieSite, 10);
      }
      const storedSite = localStorage.getItem('siteId') || localStorage.getItem('currentSite');
      if (storedSite) {
        try {
          const parsed = JSON.parse(storedSite);
          if (parsed && parsed.id) return parsed.id;
          if (typeof parsed === 'number') return parsed;
        } catch (e) {
          if (!isNaN(storedSite)) return parseInt(storedSite, 10);
        }
      }
      return 1;
    },
    editType() {
      if (!this.item) return 'General';
      if (this.item.appointmentType === 'CorteCesto') return 'CorteCesto';
      if (this.item.appointmentType === 'CorteRefugo') return 'CorteRefugo';
      if (this.item.appointmentType === 'Finalizacao') return 'Finalizacao';
      if (this.item.extrusionDetails != null || this.item.appointmentType === 'Tarugo') return 'Tarugo';
      return 'General';
    },
    modalTitle() {
      switch (this.editType) {
        case 'Tarugo':
          return 'Editar Apontamento — Extrusão / Tarugo';
        case 'Finalizacao':
          return 'Editar Apontamento — Finalização';
        case 'CorteCesto':
          return 'Editar Cesto do Corte';
        case 'CorteRefugo':
          return 'Editar Refugo do Corte';
        default:
          return `Editar Apontamento — ${this.item?.statusName || 'Status / Parada'}`;
      }
    },
    stopStatusList() {
      if (!Array.isArray(this.statusList)) return [];

      let list = this.statusList.filter(e => {
        const classId = Number(e.status_classification_id ?? e.statusClassificationId);
        const name = (e.name || '').trim();
        const id = Number(e.id);

        // Exclui status do sistema / ingleses / executando / iniciado / finalizado
        const invalidNames = ['not started', 'running', 'complete', 'started', 'iniciado', 'executando', 'finalizado'];
        if (invalidNames.includes(name.toLowerCase())) return false;
        if (id === 1 || id === 3 || id === 5) return false;

        // Regra padrão de Paradas do SFC: status_classification_id em 1 (com setup id=2), 2 ou 3
        return (
          ((classId === 1) && (name !== 'Setup' || id === 2)) ||
          classId === 2 ||
          classId === 3
        );
      });

      // Ordena alfabeticamente como na tela de apontamentos
      list = [...list].sort((a, b) =>
        String(a.name || '').localeCompare(String(b.name || ''), 'pt', { sensitivity: 'base', numeric: true })
      );

      // Garante que o status atual do item esteja presente se for válido
      if (this.item && this.item.statusId && !list.some(s => s.id === this.item.statusId)) {
        list.unshift({
          id: this.item.statusId,
          name: this.item.statusName || 'Status Atual'
        });
      }

      return list;
    },
    computedAlloyOptions() {
      const base = (this.orderObject?.availableAlloys && Array.isArray(this.orderObject.availableAlloys) && this.orderObject.availableAlloys.length > 0)
        ? this.orderObject.availableAlloys
        : (this.alloyOptions || []);
      const list = [...base];

      const ordAlloy = this.orderObject?.alloy || this.orderObject?.productParameters?.liga;
      if (ordAlloy && ordAlloy !== '-' && !list.includes(ordAlloy)) {
        list.push(ordAlloy);
      }
      if (this.form.alloy && !list.includes(this.form.alloy)) {
        list.push(this.form.alloy);
      }
      const unique = Array.from(new Set(list.filter(x => x && x !== '-')));
      return unique.length > 0 ? unique : (this.form.alloy ? [this.form.alloy] : ['6063']);
    },
    computedOriginOptions() {
      const list = ['Alumasa', 'Importado'];
      if (this.form.origin && !list.includes(this.form.origin)) {
        list.push(this.form.origin);
      }
      return list;
    },
    isFormValid() {
      if (this.submitting) return false;
      if (this.editType === 'Tarugo') {
        return (
          this.form.alloy &&
          this.form.heatNumber &&
          this.form.origin &&
          this.form.quantity > 0 &&
          this.form.barQuantity > 0 &&
          this.form.sizeMm > 0 &&
          this.form.pullLengthM > 0 &&
          this.form.temperature > 0 &&
          this.form.emergentTemperature > 0
        );
      }
      if (this.editType === 'Finalizacao') {
        return !!this.form.situation;
      }
      if (this.editType === 'CorteCesto') {
        return !!(this.form.basketCode && this.form.basketCode.trim() && this.form.barQuantity > 0);
      }
      if (this.editType === 'CorteRefugo') {
        return !!((this.form.quantityKg > 0 || this.form.quantityBars > 0) && this.form.refuseReasonId);
      }
      if (this.editType === 'General') {
        return !!this.form.statusId;
      }
      return true;
    }
  },
  methods: {
    open(item) {
      this.item = item;
      this.initForm(item);
      this.fetchReasons();
      if ((!this.orderObject?.availableAlloys || this.orderObject.availableAlloys.length === 0) && (!this.alloyOptions || this.alloyOptions.length === 0)) {
        this.fetchAlloys();
      }
      this.isVisible = true;
    },
    async fetchAlloys() {
      try {
        const res = await getExtrusionAlloys();
        const alloys = res.data?.data || res.data || [];
        if (Array.isArray(alloys) && alloys.length > 0) {
          this.alloyOptions = alloys;
        }
      } catch (e) {
        console.error('Erro ao buscar ligas:', e);
      }
    },
    closeModal() {
      this.isVisible = false;
      this.item = null;
      this.form = {};
    },
    initForm(item) {
      if (!item) {
        this.form = {};
        return;
      }

      if (this.editType === 'Tarugo' || item.extrusionDetails) {
        const d = item.extrusionDetails || {};
        this.form = {
          alloy: d.alloy || '6063',
          heatNumber: d.heatNumber || '',
          origin: d.origin || 'Alumasa',
          sizeMm: d.sizeMm || 0,
          quantity: d.quantity || 0,
          barQuantity: d.barQuantity || d.quantity || item.producedQuantity || 0,
          pullLengthM: d.pullLengthM || 0,
          temperature: d.temperature || 0,
          emergentTemperature: d.emergentTemperature || 0,
          extrusionSpeedMMin: d.extrusionSpeedMMin || null,
          profileWeightKgM: d.profileWeightKgM || null,
          observation: d.observation || item.observation || ''
        };
      } else if (this.editType === 'Finalizacao') {
        const d = item.extrusionDetails || {};
        this.form = {
          situation: d.situation || 'Aprovado',
          rejectionReasonId: d.rejectionReasonId || item.refuseReasonId || null,
          rejectionReason: d.rejectionReason || item.refuseReasonName || '',
          observation: d.observation || item.observation || ''
        };
      } else if (this.editType === 'CorteCesto') {
        const b = (item.cuttingBaskets && item.cuttingBaskets[0]) || {};
        let bSize = Number(b.barSizeMm) || 6000;
        if (bSize > 0 && bSize <= 50) {
          bSize *= 1000;
        }
        this.form = {
          basketCode: b.basketCode || item.statusName?.replace('Cesto ', '') || '',
          barQuantity: b.barQuantity || 0,
          barSizeMm: bSize
        };
      } else if (this.editType === 'CorteRefugo') {
        const r = (item.cuttingRefuses && item.cuttingRefuses[0]) || {};
        this.form = {
          quantityKg: r.quantityKg || item.refuseQuantity || 0,
          quantityBars: r.quantityBars || (item.refuseQuantity > 0 ? item.refuseQuantity : null),
          refuseReasonId: r.refuseReasonId || item.refuseReasonId || null,
          observation: r.observation || item.observation || ''
        };
      } else {
        this.form = {
          statusId: item.statusId || null
        };
      }
    },
    async fetchReasons() {
      try {
        const site = this.resolvedSiteId || 1;
        const response = await this.$http2.get(`/api/mysfc/operation-progress/context?siteId=${site}&operationId=${this.item?.operationId || this.orderObject?.operationId || ""}&resourceId=${this.item?.resourceId || this.orderObject?.resourceId || ""}`);
        if (response.data && response.data.data) {
          if (Array.isArray(response.data.data.refuseReasons)) {
            this.refuseReasons = response.data.data.refuseReasons.map(r => ({
              id: r.id,
              name: r.name || r.description
            }));
          }
          if (Array.isArray(response.data.data.status)) {
            this.statusList = response.data.data.status.map(s => ({
              id: s.id || s.value,
              name: s.name || s.text,
              color: s.color,
              status_classification_id: s.status_classification_id || s.statusClassificationId
            }));
          }
        }
      } catch (e) {
        this.refuseReasons = [];
      }
    },
    onRejectionReasonChange() {
      const found = this.refuseReasons.find(r => r.id === this.form.rejectionReasonId);
      this.form.rejectionReason = found ? found.name : '';
    },
    getStatusBadgeStyle(color) {
      const rawColor = (color || '#974900').trim();
      let bg = `${rawColor}1A`;
      return {
        backgroundColor: bg,
        color: rawColor,
        border: `1px solid ${rawColor}40`,
        borderRadius: '8px',
        fontWeight: '700',
        padding: '5px 14px',
        fontSize: '13px',
        lineHeight: '16px',
        fontFamily: '"Raleway", sans-serif',
        whiteSpace: 'nowrap'
      };
    },
    async submitEdit() {
      if (!this.isFormValid || !this.item) return;

      this.submitting = true;
      try {
        if (this.editType === 'CorteCesto') {
          const basketId = (this.item.cuttingBaskets && this.item.cuttingBaskets[0]?.id) || this.item.id;
          await updateCuttingBasket(basketId, {
            basketCode: this.form.basketCode,
            barQuantity: Number(this.form.barQuantity)
          });
        } else if (this.editType === 'CorteRefugo') {
          const refuseId = (this.item.cuttingRefuses && this.item.cuttingRefuses[0]?.id) || this.item.id;
          await updateCuttingRefuse(refuseId, {
            quantityKg: Number(this.form.quantityKg),
            quantityBars: this.form.quantityBars ? Number(this.form.quantityBars) : null,
            refuseReasonId: Number(this.form.refuseReasonId),
            observation: this.form.observation
          });
        } else {
          // Tarugo, Finalizacao ou Status Progress
          await updateExtrusionProgress(this.item.id, {
            statusId: this.form.statusId,
            alloy: this.form.alloy,
            heatNumber: this.form.heatNumber,
            origin: this.form.origin,
            sizeMm: this.form.sizeMm ? Number(this.form.sizeMm) : undefined,
            quantity: this.form.quantity ? Number(this.form.quantity) : undefined,
            barQuantity: this.form.barQuantity ? Number(this.form.barQuantity) : undefined,
            pullLengthM: this.form.pullLengthM ? Number(this.form.pullLengthM) : undefined,
            temperature: this.form.temperature ? Number(this.form.temperature) : undefined,
            emergentTemperature: this.form.emergentTemperature ? Number(this.form.emergentTemperature) : undefined,
            extrusionSpeedMMin: this.form.extrusionSpeedMMin ? Number(this.form.extrusionSpeedMMin) : undefined,
            profileWeightKgM: this.form.profileWeightKgM ? Number(this.form.profileWeightKgM) : undefined,
            situation: this.form.situation,
            rejectionReasonId: this.form.rejectionReasonId,
            rejectionReason: this.form.rejectionReason,
            observation: this.form.observation,
            producedQuantity: this.form.producedQuantity !== undefined ? Number(this.form.producedQuantity) : undefined,
            refuseQuantity: this.form.refuseQuantity !== undefined ? Number(this.form.refuseQuantity) : undefined,
            refuseReasonId: this.form.refuseReasonId
          });
        }

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Sucesso',
            icon: 'CheckIcon',
            text: 'Apontamento atualizado com sucesso!',
            variant: 'success'
          }
        });

        this.closeModal();
        this.$emit('saved');
      } catch (err) {
        console.error('Erro ao atualizar apontamento:', err);
        const errorMsg = err.response?.data?.message || 'Não foi possível atualizar o apontamento.';
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro',
            icon: 'AlertTriangleIcon',
            text: errorMsg,
            variant: 'danger'
          }
        });
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.extrusion-edit-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.65);
  z-index: 2200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  overflow-y: auto;
}

.extrusion-edit-modal-dialog {
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #cfc4be;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  width: 100%;
  max-width: 850px;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow: hidden;
  animation: modalScaleIn 0.2s ease-out;

  .modal-dialog-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid #ede8e4;
    background: #faf9f8;

    .modal-dialog-title {
      font-size: 17px;
      font-weight: 700;
      color: #4c4541;
    }

    .btn-close-modal {
      background: transparent;
      border: none;
      color: #998f8a;
      cursor: pointer;
      padding: 4px;
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;

      &:hover {
        background: #eee7e0;
        color: #4c4541;
      }
    }
  }

  .modal-dialog-body {
    padding: 18px 20px;
    overflow-y: auto;
    flex: 1;
  }

  .modal-dialog-footer {
    background: #faf9f8;
    flex-shrink: 0;
  }

  .text-copper {
    color: #974900 !important;
  }

  .text-orange {
    color: #e65100 !important;
  }

  .text-red {
    color: #d84315 !important;
  }

  .input-label {
    font-size: 12px;
    font-weight: 700;
    color: #4c4541;
    margin-bottom: 4px;
    display: block;
  }

  .operator-input {
    height: 42px;
    padding: 0 12px;
    background: #fafafa;
    border: 1px solid #cfc4be;
    border-radius: 6px;
    font-size: 14px;
    color: #4c4541;
    transition: border-color 0.2s;

    &:focus {
      border-color: #974900;
      outline: none;
      background: #fff;
    }
  }

  .btn-operator-secondary {
    height: 42px;
    background: #fafafa;
    border: 1px solid #cfc4be;
    border-radius: 6px;
    color: #4c4541;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: #eee;
    }
  }

  .btn-operator-confirm {
    height: 42px;
    background: #974900;
    border: 1px solid #974900;
    border-radius: 6px;
    color: #fff;
    font-weight: 700;
    font-size: 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s;

    &:hover:not(:disabled) {
      background: #7a3b00;
      border-color: #7a3b00;
    }

    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
  }
}

@keyframes modalScaleIn {
  from {
    opacity: 0;
    transform: scale(0.96);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
